import { atom } from 'jotai';

import type { EvidenceSBSCandidate } from '../../contracts/side-by-side-evidence/types';
import { makeInitialStreamState, type SBSStreamState } from './streamState';

export const evidenceSBSExperimentModeAtom = atom('');
export const evidenceSBSDisplayedTimeAtom = atom<Date | null>(null);
export const evidenceSBSUserQueryAtom = atom('');
export const evidenceSBSMessagesAtom = atom<Array<{ role: string; content: string }>>([]);
export const evidenceSBSChatIdAtom = atom<string | null>(null);
export const evidenceSBSSessionIdAtom = atom<string | null>(null);
export const evidenceSBSCandidatesAtom = atom<EvidenceSBSCandidate[]>([]);

/**
 * EVI-1701 follow-up: SBS stream state lives in Jotai (not component
 * `useState`) so the two in-flight answer streams survive the Evidence panel
 * unmounting when the user switches tabs and back. Normal Evidence Q&A already
 * persists this way through `chatStateAtom`; SBS previously kept stream state
 * in `useEvidenceSBSStreams`'s local state and aborted both streams on unmount,
 * so a tab switch cancelled the answer. These atoms + `getDefaultStore()`
 * writes from the SSE callbacks let the streams keep filling in while the panel
 * is unmounted, exactly like the single-stream path.
 */
export const evidenceSBSStreamAAtom = atom<SBSStreamState>(makeInitialStreamState());
export const evidenceSBSStreamBAtom = atom<SBSStreamState>(makeInitialStreamState());

/**
 * The chatId the current SBS round's streams were started for. Persisted (vs a
 * component ref) so remounting the panel after a tab switch does NOT restart
 * the streams — the guard sees they're already running for this chat.
 */
export const evidenceSBSStartedChatIdAtom = atom<string | null>(null);

/**
 * AbortControllers for the in-flight SBS streams. Held in Jotai so they
 * outlive the panel's mount and can be aborted from `resetEvidenceSBSAction`
 * (dismiss / skip / confirm / new round) — the only places we genuinely want
 * the streams torn down. Unmount no longer aborts.
 */
export const evidenceSBSAbortControllersAtom = atom<AbortController[]>([]);

export const shouldDoEvidenceSBSAtom = atom(
  (get) => get(evidenceSBSCandidatesAtom).length > 1
);

/**
 * EVI-1602: true while the SBS comparison overlay is on screen — i.e. once the
 * candidates have loaded (comparison / confirming / skipping, until
 * `resetEvidenceSBSAction` clears them). Consumed by the chat timeline so it
 * can tighten the first message's top spacing only while the overlay is up,
 * without affecting normal chats.
 */
export const isEvidenceSBSActiveAtom = atom((get) => get(shouldDoEvidenceSBSAtom));
